import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRight,
  Check,
  ChevronDown,
  Inbox,
  Loader2,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Undo2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Aviso } from '@/components/ui/aviso';
import { Etiqueta } from '@/components/ui/etiqueta';
import { CeldaProducto } from '@/components/ui/celda-producto';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { CambioDePrecio, sentidoDe } from '@/components/ui/precio';
import { SegmentosCompite } from '@/features/compite/ui/segmentos-compite';
import { BarraDeLote } from '@/features/compite/ui/barra-de-lote';
import { cn } from '@/lib/utils';
import {
  cantidadDe,
  formatoCantidad,
  formatoFechaYHora,
  formatoPorcentaje,
  formatoPrecio,
} from '@/lib/utils/formato-precio';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ToastAction } from '@/components/ui/toast';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { avisoDelFreno } from '@/features/mini-prixia/freno-de-la-demo';
import { priceProposalsApi } from '../services/price-proposals-api';
import { tituloDeSalida } from '@/features/pricing-rules/lib/salidas-sin-precio';
import { ESPERA_AL_TIPEAR_MS } from '@/features/pricing-rules/lib/use-pedido-con-espera';
import { FilaSinPrecio, avisoDeDesenlace } from './fila-sin-precio';
import { FiltroDeValores } from './filtro-de-valores';
import { ChipsDeEstrategia, colorDeEstrategia } from './chips-de-estrategia';
import { DialogoDescartar, MOTIVOS_DE_DESCARTE } from './dialogo-descartar';
import { CeldasDeDescartada } from './fila-descartada';
import { DetalleDeLaPropuestaAbierta } from './detalle-de-la-propuesta';
import { FichaEstrategiaDialog } from './ficha-estrategia';
import { ProductHistoryDialog } from './product-history-dialog';
import { VolverAEstePrecioDialog } from './volver-a-este-precio-dialog';
import { loDemasDelAviso } from '../lib/volver-a-este-precio';
import {
  avisoDeLasViejas,
  claseDeVieja,
  queLePasaEnFrase,
  type ClaseDeVieja,
} from '../lib/desactualizadas';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { strategyApplicationsApi } from '../services/strategy-applications-api';
import type { AppliedStrategy } from '../types/strategy-applications.types';
import { SelectNativo } from '@/components/ui/select-nativo';
import { SelectorDeFechas } from './selector-de-fechas';
import { CUALQUIER_FECHA, instantesDelRango, type RangoDeFechas } from '../lib/rango-de-fechas';
import type {
  FacetasDeLaBandeja,
  FiltroBandeja,
  FiltroDeMotivo,
  MotivoAlDescartar,
  OrigenDeLaPropuesta,
  PriceProposalRow,
  ProposalCounts,
  ProposalStatus,
  ResultadoDeLote,
  SinDeshacer,
} from '../types/price-proposals.types';
import { porQueEnTexto } from '@/features/pricing-rules/lib/recalculo';
import { BOTON_DEGRADADO } from '@/features/compite/ui/boton-degradado';
import { EnlaceAProducto } from '@/components/ui/enlace-a-producto';

/**
 * La bandeja de propuestas — Etapa 4 (plan 167, Tareas 5 y 6).
 *
 * Es donde por primera vez se puede mirar lo que el sistema propone antes de que
 * cambie nada, y también el permiso para encender las reglas en producción.
 *
 * DOS DECISIONES DE PANTALLA QUE NO SON COSMÉTICAS
 * -----------------------------------------------
 * 1. Una propuesta cuyo precio de abajo se movió sale marcada y **no se puede
 *    seleccionar**. Avisar sin bloquear es para las advertencias; esto no es una
 *    advertencia, es una propuesta que ya no aplica.
 * 2. El resultado de un lote se muestra **desglosado**. Un «listo» que esconde 12
 *    fallos es peor que un error.
 */

const PAGE_SIZE = 50;

const PESTANAS: Array<{ estado: ProposalStatus; texto: string }> = [
  { estado: 'PENDING', texto: 'Por decidir' },
  { estado: 'APPLIED', texto: 'Aprobadas' },
  { estado: 'DISCARDED', texto: 'Descartadas' },
  { estado: 'AUTO_APPLIED', texto: 'Aplicadas solas' },
];

/**
 * Sin filtro. Un objeto estable y no un `{}` nuevo: `setFiltro({})` en cada
 * montaje cambiaba `cargar` y pedía la lista dos veces.
 */
const SIN_FILTRO: FiltroBandeja = {};

/** Un filtro rápido puesto: el azul suave del elegido, como en el grupo de estados. */
const PUESTO = 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/10';

/** «Cumplir «Sony al más bajo»» → «cumplir «Sony al más bajo»»: sólo la primera letra. */
const minusculaInicial = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);

/**
 * «+3,5 % sobre el más barato» / «17,8 % bajo el más barato»: una decimal, como el
 * resto. Lo que redondea a 0,0 es «igual al más barato» (auditoría M5).
 */
function distanciaAlMasBarato(pct: number): string {
  if (Math.abs(pct) < 0.05) return 'igual al más barato';
  return pct < 0
    ? `${formatoPorcentaje(Math.abs(pct))} bajo el más barato`
    : `${formatoPorcentaje(pct, { signo: true })} sobre el más barato`;
}

const plata = formatoPrecio;

/** «25 sept, 00:57»: la hora en 24 h (auditoría I6: `toLocaleString` daba «12:57 a. m.»). */
const cuando = (iso: string) => formatoFechaYHora(iso);

/**
 * Lote B1: cómo se nombran los valores elegidos en una frase: «de Maxiking o
 * Televisores», «sin marca», «de Sony o sin marca».
 */
function deLosValores(valores: string[], sin: string): string {
  const conNombre = valores.filter((v) => v !== '');
  return [
    ...(conNombre.length > 0 ? [`de ${conNombre.join(' o ')}`] : []),
    ...(valores.includes('') ? [sin] : []),
  ].join(' o ');
}

/** Cuántas de la bandeja tienen alguno de los valores elegidos (de las facetas). */
const cuantasCon = (valores: string[], faceta: FacetasDeLaBandeja['marcas']) =>
  faceta.filter((v) => valores.includes(v.valor ?? '')).reduce((n, v) => n + v.cuantas, 0);

/**
 * Lote B1 (maqueta `b1-bandeja`, «Sin resultados con los filtros»): por qué no
 * hay ninguna con la categoría y la marca elegidas, con cuántas hay de cada una.
 */
export function vacioPorValores(
  facetas: FacetasDeLaBandeja,
  categorias: string[],
  marcas: string[]
): { titulo: string; detalle: string } {
  const partes = [
    ...(categorias.length > 0
      ? [
          {
            que: deLosValores(categorias, 'sin categoría'),
            n: cuantasCon(categorias, facetas.categorias),
          },
        ]
      : []),
    ...(marcas.length > 0
      ? [{ que: deLosValores(marcas, 'sin marca'), n: cuantasCon(marcas, facetas.marcas) }]
      : []),
  ];
  const total = formatoCantidad(facetas.total);
  // Con una sola: «La única no es…».
  const ningunaEs = facetas.total === 1 ? 'La única no es' : `Ninguna de las ${total} es`;
  return {
    titulo:
      partes.length === 2
        ? `${ningunaEs} ${partes[0].que} y ${partes[1].que} a la vez`
        : `${ningunaEs} ${partes[0]?.que ?? ''}`.trim(),
    detalle: `Hay ${partes.map((p) => `${formatoCantidad(p.n)} ${p.que}`).join(' y ')}.`,
  };
}

/** «hace 30 días»: desde que la corrida la calculó por última vez (lote A, A1). */
const haceDias = (f: PriceProposalRow) => `hace ${formatoCantidad(f.diasDesdeElCalculo ?? 0)} días`;

/**
 * Lote A del 2-oct (A1): pendiente, decidible, y debajo del costo o vieja. No va
 * en el lote: se aprueba sola, con «Aprobar esta». Con un backend viejo (sin
 * los campos) es false: todo se tilda como antes. S3 (decisión de Sebastián del
 * 8-oct): tampoco la que usó el precio de una tienda quitada a la empresa.
 */
const vaUnaPorUna = (f: PriceProposalRow) =>
  f.status === 'PENDING' &&
  !f.desactualizada &&
  !f.costoCambio &&
  !f.sinPrecioPosible &&
  // Tarea 8 (ronda 1): el detalle dice «Ya no aplica»; la fila no ofrece aprobarla.
  !(f.yaNoAplica ?? false) &&
  (!!f.debajoDelCosto || !!f.vieja || !!f.avisoTiendaQuitada);

/**
 * Quién decidió una resuelta, en llano (28-sep: «¿fueron descartadas
 * automáticamente o manualmente?»). La base no distingue un usuario borrado de
 * una decisión sin quién (las del sembrado de la demo): se dice, sin inventar.
 * Lote B1: las descartadas lo dicen en sus columnas (`fila-descartada.tsx`).
 */
function quienDecidio(f: PriceProposalRow): string | null {
  const quien =
    f.status === 'AUTO_APPLIED'
      ? 'Se aplicó sola'
      : f.status === 'APPLIED'
        ? f.resueltaPor
          ? `La aprobó ${f.resueltaPor}`
          : 'No quedó registrado quién la aprobó'
        : null;
  if (!quien) return null;
  return `${quien} · ${cuando(f.resolvedAt ?? f.createdAt)}`;
}

/**
 * Ronda del 5-oct (tarea 7): por qué una aprobada (o aplicada sola) no se
 * puede deshacer, en corto. Sin precio de antes no se dice nada: no hay nada
 * útil que contar (D3). El freno de verdad es el del servidor.
 */
const SIN_DESHACER_EN_TEXTO: Partial<Record<SinDeshacer, string>> = {
  ANTES_DEL_REGISTRO: 'Anterior al historial de cambios',
  SIN_CAMBIO: 'No cambió el precio',
  YA_DESHECHA: 'Ya se deshizo',
  HUBO_OTRO_CAMBIO: 'Hubo otro cambio después',
};
const SIN_DESHACER_DETALLE: Partial<Record<SinDeshacer, string>> = {
  // Ronda 1: verbos que valen igual para una aprobada y una aplicada sola.
  ANTES_DEL_REGISTRO:
    'Es de antes de que se guardara el precio de antes de cada cambio: no se puede deshacer desde acá.',
  SIN_CAMBIO: 'Con esta propuesta el precio quedó igual: no hay nada que deshacer.',
  YA_DESHECHA: 'Este cambio ya se deshizo.',
  HUBO_OTRO_CAMBIO:
    'Después de este cambio el precio cambió otra vez: para no pisarlo, abrí la fila, entrá a «Ver todo su historial» y usá «Volver a …» en el precio que quieras.',
};

interface ProposalsInboxTabProps {
  /**
   * Llega desde la pantalla Hoy: al entrar por una decisión, la bandeja se abre
   * mostrando SOLO las propuestas de esa estrategia. `null` = todas.
   */
  filtroEstrategiaInicial?: string | null;
  /** Llega desde Hoy: sólo las sin precio posible, o sólo los cambios grandes (4B-datos T4). */
  filtroInicial?: FiltroBandeja | null;
  /**
   * Plan 2 (P2-D6): abre la hoja de la vuelta de esa estrategia. Una vuelta
   * bloqueada (el precio o el costo cambió) no la recalcula nadie: se deja
   * como está desde la hoja.
   */
  onAbrirVuelta?: (strategyApplicationId: string) => void;
  /** Revisión t5 (m4): cambia cuando algo se resolvió afuera (la hoja de la vuelta). */
  refreshKey?: number;
  /**
   * Rediseño de Compite (maqueta): los cuatro estados van a la derecha del
   * encabezado de la página, no arriba de la tabla. Sin destino, van donde estaban.
   */
  destinoDelEstado?: HTMLElement | null;
  /**
   * Cada vez que la bandeja vuelve a contar (al entrar, al aprobar, descartar o
   * deshacer). Compite lo usa para el número del título y el del menú.
   */
  onConteo?: (conteo: ProposalCounts) => void;
}

/** Lo pinta en `destino` (por portal) si hay uno; si no, donde está. */
function conDestino(destino: HTMLElement | null, contenido: ReactNode) {
  return destino ? createPortal(contenido, destino) : contenido;
}

export function ProposalsInboxTab({
  filtroEstrategiaInicial = null,
  filtroInicial = null,
  onAbrirVuelta,
  refreshKey = 0,
  onConteo,
  destinoDelEstado = null,
}: ProposalsInboxTabProps = {}) {
  const { toast } = useToast();
  // Soporte («Entrar como») en una demo sólo mira: no aprueba, descarta ni devuelve propuestas.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());
  // En una ref: la carga no se vuelve a armar si quien la usa pasa una función nueva.
  const alContar = useRef(onConteo);
  alContar.current = onConteo;
  const { confirm, ConfirmDialog } = useConfirmDialog();
  // Deshacer (ronda del 5-oct): el mismo permiso que `/volver` (COMPANY_ADMIN o
  // PLATFORM_ADMIN). Un APPROVER aprueba pero no deshace.
  const { isAdmin } = useAuth();

  // «Ver cuáles» de Hoy (4B-datos T4) abre en «Aplicadas solas».
  const [estado, setEstado] = useState<ProposalStatus>(filtroInicial?.status ?? 'PENDING');
  const [estrategia, setEstrategia] = useState<string | null>(filtroEstrategiaInicial);
  // Ola final R1: el nombre de la elegida se guarda al elegirla. Las facetas
  // de otra pestaña pueden no traerla (tiene 0 ahí) y la lista puede volver
  // vacía: sin esto la opción huérfana decía «La elegida (0)».
  const [nombreElegido, setNombreElegido] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<FiltroBandeja>(filtroInicial ?? SIN_FILTRO);
  const [decidiendo, setDecidiendo] = useState<PriceProposalRow | null>(null);
  const [pagina, setPagina] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  // Lote B1: categoría y marca, de elección múltiple ('' = sin). Valen en las
  // cuatro pestañas: cambiar de pestaña no los saca.
  const [categorias, setCategorias] = useState<string[]>([]);
  const [marcas, setMarcas] = useState<string[]>([]);
  const [facetas, setFacetas] = useState<FacetasDeLaBandeja | null>(null);
  // Lote B1 (D2): Descartadas por motivo, y la ventana del motivo al descartar
  // (de un lote, o de una fila: `fila` es la que se descarta sola).
  const [motivoDescarte, setMotivoDescarte] = useState<FiltroDeMotivo | null>(null);
  // Lote B2 (punto 7): Aprobadas y Descartadas, por cuándo se decidieron.
  const [decidida, setDecidida] = useState<RangoDeFechas>(CUALQUIER_FECHA);
  // Ronda final de R4 (QA DS5): Aplicadas solas también se filtra por cuándo se aplicó.
  const seDecidio = estado === 'APPLIED' || estado === 'DISCARDED' || estado === 'AUTO_APPLIED';
  // Spec 275 (R2, D-R2-9): cuándo se calculó, en Por decidir.
  const [calculada, setCalculada] = useState<RangoDeFechas>(CUALQUIER_FECHA);
  // `null` = como venga: abiertos si hay alguno puesto (rediseño de Compite).
  const [filtrosAbiertos, setFiltrosAbiertos] = useState<boolean | null>(null);
  // Lote C1 (punto 8): por origen. Vale en las cuatro pestañas, como la categoría;
  // se llega con «Origen: recálculo» desde Hoy o al terminar de recalcular.
  const [origen, setOrigen] = useState<OrigenDeLaPropuesta | null>(filtroInicial?.origen ?? null);
  const [aDescartar, setADescartar] = useState<{
    ids: string[];
    fila: PriceProposalRow | null;
  } | null>(null);
  // Lote B1 (punto 2): la fila abierta (una a la vez), la ficha de su
  // estrategia y el historial del producto, que se abren desde ella.
  const [abierta, setAbierta] = useState<string | null>(null);
  const [fichaDe, setFichaDe] = useState<AppliedStrategy | null>(null);
  const [historialDe, setHistorialDe] = useState<PriceProposalRow | null>(null);
  /** Tarea 7: la aprobada (o aplicada sola) que se está por deshacer. */
  const [aDeshacer, setADeshacer] = useState<PriceProposalRow | null>(null);
  // Ronda final B1: «Ver la estrategia» deshabilitado mientras abre su ficha.
  const [abriendoFicha, setAbriendoFicha] = useState(false);

  const [filas, setFilas] = useState<PriceProposalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [desactualizadas, setDesactualizadas] = useState(0);
  const [conteo, setConteo] = useState<ProposalCounts | null>(null);

  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [ultimoLote, setUltimoLote] = useState<ResultadoDeLote | null>(null);

  // Lote A del 2-oct (A6): se busca mientras se escribe. Cada pedido lleva su
  // número; la respuesta de uno que ya no es el último se descarta (si no, una
  // búsqueda lenta de «tec» pisaría a la de «teclado» que llegó antes).
  const ultimoPedido = useRef(0);
  // Lote B1 (auditoría del 3-oct): las facetas se piden sólo si cambió lo que
  // cuentan (no al cambiar de página, ni de categoría o marca, que no cuentan),
  // o después de resolver algo (`recargar`).
  const facetasDe = useRef<string | null>(null);

  const cargar = useCallback(async () => {
    const mio = ++ultimoPedido.current;
    setCargando(true);
    setError(null);
    try {
      const filtros = {
        status: estado,
        search: busquedaAplicada || undefined,
        strategyApplicationId: estrategia ?? undefined,
        sinPrecioPosible: filtro.sinPrecioPosible || undefined,
        motivo: filtro.motivo,
        origen: origen ?? undefined,
        limitadaPorRegla: filtro.limitadaPorRegla || undefined,
        dias: filtro.dias,
        productId: filtro.productId,
        motivoDescarte: (estado === 'DISCARDED' && motivoDescarte) || undefined,
        ...(seDecidio
          ? {
              decididaDesde: instantesDelRango(decidida).desde,
              decididaHasta: instantesDelRango(decidida).hasta,
            }
          : {}),
        ...(estado === 'PENDING'
          ? {
              calculadaDesde: instantesDelRango(calculada).desde,
              calculadaHasta: instantesDelRango(calculada).hasta,
            }
          : {}),
      };
      // El motivo tampoco cambia lo que cuentan (las facetas no se filtran por él).
      const clave = JSON.stringify({ ...filtros, motivoDescarte: undefined, refreshKey });
      const conFacetas = clave !== facetasDe.current;
      const [data, cuentas, valores] = await Promise.all([
        priceProposalsApi.listar({
          ...filtros,
          page: pagina,
          pageSize: PAGE_SIZE,
          categorias,
          marcas,
        }),
        priceProposalsApi.contar(),
        // Lote B1: sin las facetas la lista se ve igual; sólo no se ofrecen
        // los filtros. Un error acá no tumba la bandeja.
        conFacetas
          ? Promise.resolve()
              .then(() => priceProposalsApi.facetas(filtros))
              .catch(() => null)
          : undefined,
      ]);
      if (mio !== ultimoPedido.current) return;
      setFilas(data.items);
      // Lote C2b: llegar a UN producto abre su fila (la que espera tu decisión).
      if (filtros.productId && data.items.length > 0) setAbierta(data.items[0].id);
      setTotal(data.total);
      setDesactualizadas(data.desactualizadas);
      setConteo(cuentas);
      alContar.current?.(cuentas);
      if (conFacetas) {
        setFacetas(valores ?? null);
        // Si no llegaron, se vuelven a pedir la próxima vez.
        facetasDe.current = valores ? clave : null;
      }
      setSeleccion(new Set());
    } catch (e) {
      if (mio !== ultimoPedido.current) return;
      setError(mensajeDeError(e, 'No se pudieron traer las propuestas. Probá de nuevo.'));
    } finally {
      if (mio === ultimoPedido.current) setCargando(false);
    }
  }, [
    estado,
    seDecidio,
    pagina,
    busquedaAplicada,
    estrategia,
    filtro,
    categorias,
    marcas,
    motivoDescarte,
    decidida,
    calculada,
    origen,
    refreshKey,
  ]);

  /** Después de resolver algo: la lista y también las facetas (cambiaron los números). */
  const recargar = useCallback(() => {
    facetasDe.current = null;
    return cargar();
  }, [cargar]);

  useEffect(() => {
    void cargar();
  }, [cargar, refreshKey]);

  // Lote B1: otra lista (pestaña, página, búsqueda, filtros) cierra la fila abierta.
  useEffect(() => {
    setAbierta(null);
  }, [
    estado,
    pagina,
    busquedaAplicada,
    estrategia,
    filtro,
    categorias,
    marcas,
    motivoDescarte,
    decidida,
    calculada,
    origen,
  ]);

  // A6: lo escrito se aplica cuando se deja de tipear (la misma espera que el
  // resto de los pedidos mientras se escribe), y vuelve a la página 1. Sin
  // mínimo de letras: un código puede ser corto y la lista va paginada.
  useEffect(() => {
    const texto = busqueda.trim();
    if (texto === busquedaAplicada) return;
    const t = setTimeout(() => {
      setBusquedaAplicada(texto);
      setPagina(1);
    }, ESPERA_AL_TIPEAR_MS);
    return () => clearTimeout(t);
  }, [busqueda, busquedaAplicada]);

  // Venir de Hoy por OTRA decisión tiene que cambiar el filtro y volver a la
  // primera página: sin esto, la segunda visita mostraría la estrategia anterior.
  useEffect(() => {
    setEstrategia(filtroEstrategiaInicial);
    // Otra estrategia (u otra visita desde Hoy): el nombre guardado no aplica.
    setNombreElegido(null);
    setFiltro(filtroInicial ?? SIN_FILTRO);
    setOrigen(filtroInicial?.origen ?? null);
    if (filtroInicial?.status) setEstado(filtroInicial.status);
    setPagina(1);
  }, [filtroEstrategiaInicial, filtroInicial]);

  // Ola final R1: el nombre de la elegida se guarda también cuando aparece en
  // las facetas o en las filas (llegando de Hoy no pasó por el selector): si
  // después cambia a una pestaña donde tiene 0, la opción huérfana lo dice.
  const nombreEnFacetas = estrategia
    ? (facetas?.estrategias ?? []).find((x) => x.valor === estrategia)?.nombre
    : undefined;
  const nombreEnFilas = estrategia
    ? filas.find((f) => f.strategyApplicationId === estrategia)?.strategyName
    : undefined;
  useEffect(() => {
    const nombre = nombreEnFacetas ?? nombreEnFilas;
    if (nombre) setNombreElegido(nombre);
  }, [nombreEnFacetas, nombreEnFilas]);

  /**
   * Spec 275 (R1-b): cuántas viejas de cada clase trae esta página, para el
   * aviso de arriba. «Ya no aplica» lo dice el backend por fila (`yaNoAplica`,
   * el mismo criterio que Hoy); una fila vieja sin el campo cae al precio
   * cambiado. Las de una estrategia que su corrida no va a recalcular (el
   * servidor manda `noLaRecalculaPorque`) cuentan como SIN_CORRIDA: a esas no
   * se les promete «su próxima corrida».
   */
  const cuantas = useMemo(() => {
    const c: Record<ClaseDeVieja, number> = {
      DE_ESTRATEGIA: 0,
      SIN_CORRIDA: 0,
      VUELTA: 0,
      DEL_RECALCULO: 0,
    };
    for (const f of filas)
      if (f.status === 'PENDING' && (f.yaNoAplica ?? f.desactualizada)) c[claseDeVieja(f)] += 1;
    return c;
  }, [filas]);

  /**
   * Solo las que se pueden decidir en lote: pendientes, con el precio y el costo
   * de abajo intactos (costoCambio: Task 0, ajuste 1; si no, el lote vuelve a
   * fallar con COSTO_CAMBIO en cada intento), y que NO sean sin precio posible.
   * Aprobarlas en lote aplicaría la salida del margen sin que nadie la
   * eligiera: se deciden una por una. Lote A del 2-oct (A1): tampoco las que
   * quedan debajo del costo ni las viejas (`vaUnaPorUna`).
   */
  const seleccionables = useMemo(
    () =>
      filas.filter(
        (f) =>
          f.status === 'PENDING' &&
          !f.desactualizada &&
          !f.costoCambio &&
          !f.sinPrecioPosible &&
          !(f.yaNoAplica ?? false) &&
          !vaUnaPorUna(f)
      ),
    [filas]
  );

  const todasElegidas = seleccionables.length > 0 && seleccion.size === seleccionables.length;

  function alternarTodas() {
    setSeleccion(todasElegidas ? new Set() : new Set(seleccionables.map((f) => f.id)));
  }

  function alternarUna(id: string) {
    setSeleccion((antes) => {
      const nueva = new Set(antes);
      if (nueva.has(id)) nueva.delete(id);
      else nueva.add(id);
      return nueva;
    });
  }

  async function correrLote(accion: 'aprobar' | 'descartar', motivo?: MotivoAlDescartar) {
    // Lote B1 (ronda final): al descartar van las que se guardaron al abrir la
    // ventana del motivo, no la selección de ahora (pudo cambiar desde entonces).
    const ids = accion === 'descartar' ? (aDescartar?.ids ?? [...seleccion]) : [...seleccion];
    if (ids.length === 0) return;

    setTrabajando(true);
    setUltimoLote(null);
    try {
      // Saneamiento del 26-sep (T7): el precio que mostraba cada fila. Si la
      // corrida reescribió alguna mientras se miraba, ésa no se aplica.
      const vistos = Object.fromEntries(
        filas.filter((f) => seleccion.has(f.id)).map((f) => [f.id, f.priceAfter])
      );
      const resultado =
        accion === 'aprobar'
          ? await priceProposalsApi.aprobarLote(ids, vistos)
          : await priceProposalsApi.descartarLote(
              ids,
              motivo,
              // Spec 275 (R3): el precio visto de las que se descartan, no de la selección.
              Object.fromEntries(
                filas.filter((f) => ids.includes(f.id)).map((f) => [f.id, f.priceAfter])
              )
            );

      setUltimoLote(resultado);

      const bien = resultado.porDesenlace.APLICADA ?? resultado.porDesenlace.DESCARTADA ?? 0;
      const mal = resultado.total - bien;
      const descartadas = resultado.resultados
        .filter((r) => r.desenlace === 'DESCARTADA')
        .map((r) => r.id);

      if (accion === 'descartar') {
        avisoDeDescarte(descartadas, mal);
      } else {
        toast({
          title: `${bien} precio${bien === 1 ? '' : 's'} actualizado${bien === 1 ? '' : 's'}`,
          description:
            mal > 0
              ? // Lote A (auditoría M6): decía «no se pudoieron».
                `${mal} ${mal === 1 ? 'no se pudo' : 'no se pudieron'} aplicar. El detalle está abajo.`
              : undefined,
          variant: mal > 0 ? 'destructive' : undefined,
        });
      }

      await recargar();
    } catch (e) {
      toast({
        title: 'No se pudo completar',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setTrabajando(false);
    }
  }

  /**
   * Deshace descartes (28-sep): vuelven a Por decidir una por una, y se dice
   * cuántas volvieron y, de las que no, por qué. Spec 275 (R1-a): una vieja
   * vuelve recalculada con su estrategia y las reglas de hoy, y se avisa con
   * el precio nuevo.
   */
  async function devolver(ids: string[]) {
    setTrabajando(true);
    try {
      const resultados = [];
      for (const id of ids) resultados.push(await priceProposalsApi.volverAPorDecidir(id));
      const volvieron = resultados.filter(
        (r) => r.desenlace === 'DEVUELTA' || r.desenlace === 'RECALCULADA'
      );
      const noVolvieron = resultados.filter(
        (r) => r.desenlace !== 'DEVUELTA' && r.desenlace !== 'RECALCULADA'
      );
      if (volvieron.length > 0) {
        const una = volvieron.length === 1 ? volvieron[0] : null;
        // Ronda de arreglos: si la única que volvió vino recalculada y otras no,
        // el aviso dice las dos cosas («Ahora propone… . N no: …»), no sólo una.
        const lasQueNo =
          noVolvieron.length > 0
            ? `${noVolvieron.length} no: ${noVolvieron[0].motivo ?? 'probá de nuevo.'}`
            : null;
        // Ola final: con dos o más vueltas, alguna recalculada, el título suma
        // cuántas volvieron recalculadas («3 volvieron… (1 recalculada)»).
        const recalculadas = volvieron.filter((r) => r.desenlace === 'RECALCULADA').length;
        const descripcion =
          una?.desenlace === 'RECALCULADA'
            ? [
                una.precioNuevo !== undefined
                  ? `Ahora propone ${formatoPrecio(una.precioNuevo)}.`
                  : null,
                lasQueNo,
              ]
                .filter(Boolean)
                .join(' ') || undefined
            : (lasQueNo ?? undefined);
        toast({
          title:
            una?.desenlace === 'RECALCULADA'
              ? 'Volvió a Por decidir, recalculada'
              : volvieron.length === 1
                ? 'Volvió a Por decidir'
                : `${volvieron.length} volvieron a Por decidir${recalculadas > 0 ? ` (${recalculadas} recalculada${recalculadas === 1 ? '' : 's'})` : ''}`,
          description: descripcion,
        });
      } else {
        toast({
          title: 'No volvió a Por decidir',
          description: noVolvieron[0]?.motivo ?? 'Probá de nuevo.',
          variant: 'destructive',
        });
      }
      await recargar();
    } catch (e) {
      toast({
        title: 'No volvió a Por decidir',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setTrabajando(false);
    }
  }

  /**
   * El aviso del descarte (28-sep), con su Deshacer: un clic de más se revierte
   * desde acá. Lo usa el lote y el descarte una por una de la fila (ronda final).
   */
  function avisoDeDescarte(descartadas: string[], conError: number) {
    const bien = descartadas.length;
    toast({
      title: `${bien} propuesta${bien === 1 ? '' : 's'} descartada${bien === 1 ? '' : 's'}`,
      description:
        conError > 0
          ? // Lote A (auditoría M6): decía «no se pudoieron».
            `${conError} ${conError === 1 ? 'no se pudo' : 'no se pudieron'} aplicar. El detalle está abajo.`
          : undefined,
      variant: conError > 0 ? 'destructive' : undefined,
      action:
        descartadas.length > 0 ? (
          <ToastAction altText="Deshacer el descarte" onClick={() => void devolver(descartadas)}>
            Deshacer
          </ToastAction>
        ) : undefined,
    });
  }

  function pedirConfirmacion(accion: 'aprobar' | 'descartar') {
    const n = seleccion.size;
    if (accion === 'aprobar') {
      confirm({
        title: `¿Actualizar ${n} precio${n === 1 ? '' : 's'}?`,
        description:
          n === 1
            ? 'El precio del producto va a cambiar al valor propuesto.'
            : `Los precios de ${n} productos van a cambiar a los valores propuestos. Queda registrado quién lo aprobó y cuándo.`,
        confirmText: 'Sí, actualizar',
        onConfirm: () => correrLote('aprobar'),
      });
    } else {
      // Lote B1 (D2): la ventana del motivo reemplaza la pregunta de sí o no.
      setADescartar({ ids: [...seleccion], fila: null });
    }
  }

  /**
   * Lote A del 2-oct (A1): aprobar UNA que no va en el lote, mirándola. Debajo
   * del costo, la ventana dice cuánto se pierde y aprobar manda `aceptaPerdida`
   * (la confirmación explícita); la vieja dice hace cuánto se calculó.
   */
  function pedirAprobarUna(f: PriceProposalRow) {
    const conPerdida = !!f.debajoDelCosto && f.perdidaPorUnidad != null && f.costoHoy != null;
    const registro = 'Queda registrado quién lo aprobó y cuándo.';
    const tambienVieja = f.vieja
      ? ` Además, se calculó ${haceDias(f)} y el mercado cambió desde entonces.`
      : '';
    confirm({
      title: `¿Aprobar ${plata(f.priceAfter)} para «${f.productName}»?`,
      description: conPerdida
        ? `Queda debajo del costo (${plata(f.costoHoy!)}): perdés ${plata(f.perdidaPorUnidad!)} por unidad.${tambienVieja} ${registro}`
        : f.vieja
          ? `Se calculó ${haceDias(f)} y el mercado cambió desde entonces: mirá si sigue teniendo sentido. ${registro}`
          : // S3 (8-oct): la que usó una tienda quitada repite su aviso.
            `${f.avisoTiendaQuitada} ${registro}`,
      confirmText: conPerdida ? 'Sí, aprobar con pérdida' : 'Sí, aprobar',
      variant: conPerdida ? 'destructive' : 'default',
      onConfirm: () => aprobarYa(f, conPerdida),
    });
  }

  /** Aprueba UNA con el precio que se ve (y, si queda debajo del costo, con la pérdida aceptada). */
  async function aprobarYa(f: PriceProposalRow, conPerdida: boolean) {
    setTrabajando(true);
    try {
      // Reglas de la demo (343): aprobar sobre un producto propio frena con 403; el aviso trae el plan.
      const r = await priceProposalsApi
        .aprobar(f.id, undefined, f.priceAfter, conPerdida)
        .catch((e: unknown) => {
          const aviso = avisoDelFreno(e);
          if (!aviso) throw e;
          toast(aviso);
          return null;
        });
      if (!r) return;
      toast(avisoDeDesenlace(r, f.priceAfter));
      await recargar();
    } catch (e) {
      toast({
        title: 'No se pudo aplicar',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setTrabajando(false);
    }
  }

  /**
   * Lote B1 (punto 2): «Aprobar X» de la fila abierta. Una que va una por una
   * (debajo del costo o vieja) pasa por la misma ventana de «Aprobar esta».
   */
  function pedirAprobarDesdeLaFila(f: PriceProposalRow) {
    if (vaUnaPorUna(f)) return pedirAprobarUna(f);
    confirm({
      title: `¿Aprobar ${plata(f.priceAfter)} para «${f.productName}»?`,
      description:
        'El precio del producto va a cambiar al valor propuesto. Queda registrado quién lo aprobó y cuándo.',
      confirmText: 'Sí, aprobar',
      onConfirm: () => aprobarYa(f, false),
    });
  }

  /** «Ver la estrategia»: su ficha, la misma de Estrategias. */
  async function verLaEstrategia(id: string) {
    // Mientras abre, el botón queda deshabilitado: un doble clic no repite el pedido.
    setAbriendoFicha(true);
    try {
      const estrategiaVista = (await strategyApplicationsApi.listar()).find((x) => x.id === id);
      if (estrategiaVista) setFichaDe(estrategiaVista);
      else
        toast({
          title: 'No se encontró la estrategia',
          description: 'Puede que se haya borrado.',
          variant: 'destructive',
        });
    } catch (e) {
      toast({
        title: 'No se pudo abrir la estrategia',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setAbriendoFicha(false);
    }
  }

  /**
   * Lote B1 (punto 2): toda la fila se abre al tocarla, una a la vez. Lo que ya
   * hace algo (la casilla, los botones, los enlaces) no la abre. Para teclado y
   * lector de pantalla va el botón de la flecha: `role="button"` sobre el `tr`
   * rompía la tabla (nested-interactive, ronda final B1).
   */
  function alternarFila(e: MouseEvent<HTMLElement>, id: string) {
    const objetivo = e.target as HTMLElement;
    if (objetivo.closest('button, a, input, textarea, select, label, [role="checkbox"]')) return;
    setAbierta((a) => (a === id ? null : id));
  }

  /**
   * Ronda final (bloqueante de integración): las que van una por una también se
   * descartan desde su fila. El aviso es el mismo del lote, con su Deshacer.
   */
  async function descartarUna(f: PriceProposalRow, motivo?: MotivoAlDescartar) {
    setTrabajando(true);
    try {
      const r = await priceProposalsApi.descartar(f.id, motivo, f.priceAfter);
      if (r.desenlace === 'DESCARTADA') {
        avisoDeDescarte([r.id], 0);
      } else {
        toast(avisoDeDesenlace(r, null));
      }
      await recargar();
    } catch (e) {
      toast({
        title: 'No se pudo descartar',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setTrabajando(false);
    }
  }

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /**
   * Lote B1 (punto 2): una fila de la tabla que se abre al tocarla, con su
   * flecha, y debajo, abierta, todo para decidir sin salir de la bandeja.
   */
  function filaQueSeAbre(f: PriceProposalRow, celdas: ReactNode, clase?: string) {
    const esta = abierta === f.id;
    // Por decidir: casilla + 6 + flecha; Descartadas: 7 + flecha; las demás: 6 + flecha.
    const columnas = estado === 'PENDING' || estado === 'DISCARDED' ? 8 : 7;
    return (
      <Fragment key={f.id}>
        {/* El mouse toca la fila entera; el teclado y el lector van por el
            botón de la flecha, que dice abrir o cerrar y apunta a la fila abierta. */}
        <TableRow
          data-state={seleccion.has(f.id) ? 'selected' : undefined}
          className={cn(
            'cursor-pointer data-[state=selected]:bg-primary/5 dark:data-[state=selected]:bg-primary/10',
            esta && 'border-b-0 bg-muted/40',
            clase
          )}
          onClick={(e) => alternarFila(e, f.id)}
        >
          {celdas}
          <TableCell className="w-12 pr-5 align-middle">
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-expanded={esta}
              aria-controls={`fila-abierta-${f.id}`}
              aria-label={
                esta
                  ? `Cerrar el detalle de ${f.productName}`
                  : `Abrir el detalle de ${f.productName}`
              }
              onClick={() => setAbierta((a) => (a === f.id ? null : f.id))}
            >
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform',
                  esta && 'rotate-180'
                )}
              />
            </button>
          </TableCell>
        </TableRow>
        {esta && (
          <TableRow
            data-testid="fila-abierta"
            id={`fila-abierta-${f.id}`}
            className="hover:bg-transparent"
          >
            <TableCell colSpan={columnas} className="bg-muted/40 px-5 pb-5 pt-1">
              <div className="rounded-tarjeta border bg-card p-5 shadow-sm">
                <DetalleDeLaPropuestaAbierta
                  fila={f}
                  trabajando={trabajando}
                  abriendoFicha={abriendoFicha}
                  onAprobar={pedirAprobarDesdeLaFila}
                  onDescartar={(x) => setADescartar({ ids: [x.id], fila: x })}
                  onDecidir={setDecidiendo}
                  onVerEstrategia={(id) => void verLaEstrategia(id)}
                  onVerHistorial={setHistorialDe}
                />
              </div>
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  }

  /** Un filtro puesto (rápido, «Ver cuáles» o de estrategia): el vacío no es «no hay nada». */
  const hayFiltro =
    !!estrategia ||
    !!filtro.sinPrecioPosible ||
    !!filtro.motivo ||
    !!filtro.limitadaPorRegla ||
    !!filtro.dias ||
    !!filtro.productId ||
    !!origen ||
    (estado === 'DISCARDED' && !!motivoDescarte) ||
    (seDecidio && (decidida.desde != null || decidida.hasta != null)) ||
    (estado === 'PENDING' && (calculada.desde != null || calculada.hasta != null));
  const vacioFiltrado = !cargando && !error && filas.length === 0 && hayFiltro;
  // Ronda final (A14): el aviso del recorte nombra la estrategia, con lo que ya
  // trae la lista (sin pedirla aparte). El nombre guardado al elegirla responde
  // cuando la lista viene vacía (0 en esta pestaña).
  const nombreDeLaEstrategia = estrategia
    ? (filas.find((f) => f.strategyApplicationId === estrategia)?.strategyName ?? nombreElegido)
    : null;
  const avisoDelFiltro = nombreDeLaEstrategia
    ? `Mostrando solo las propuestas de «${nombreDeLaEstrategia}».`
    : 'Mostrando solo las propuestas de una estrategia.';
  function quitarFiltros() {
    setFiltro(SIN_FILTRO);
    setEstrategia(null);
    setNombreElegido(null);
    setMotivoDescarte(null);
    setDecidida(CUALQUIER_FECHA);
    setCalculada(CUALQUIER_FECHA);
    setOrigen(null);
    setPagina(1);
  }

  // Lote B1: la categoría, la marca y (en Descartadas) el motivo puestos, con
  // su «Mostrando N de M» y su × (auditoría del 3-oct: también el motivo).
  const motivoPuesto = estado === 'DISCARDED' ? motivoDescarte : null;
  const hayCategoriaOMarca = categorias.length > 0 || marcas.length > 0;
  const hayValores = hayCategoriaOMarca || motivoPuesto != null || origen != null;
  const cambiarCategorias = (v: string[]) => {
    setCategorias(v);
    setPagina(1);
  };
  const cambiarMarcas = (v: string[]) => {
    setMarcas(v);
    setPagina(1);
  };
  function sacarLosValores() {
    setCategorias([]);
    setMarcas([]);
    setMotivoDescarte(null);
    setOrigen(null);
    setPagina(1);
  }
  const vacioDeValores =
    hayCategoriaOMarca && facetas && !cargando && !error && filas.length === 0
      ? vacioPorValores(facetas, categorias, marcas)
      : null;

  // La barra de lote de la maqueta: cuántas, cuánto se mueven en promedio y a
  // qué margen quedan. Sólo con lo que trae cada fila (sin costo, no se inventa).
  const elegidas = filas.filter((f) => seleccion.has(f.id));
  const resumenDelLote = resumenDeLasElegidas(elegidas);

  /** Las que no tienen su chip: se avisa arriba de la tabla, como antes. */
  // Cuántos filtros de la franja están puestos (para el botón «Filtros»).
  const filtrosPuestos =
    categorias.length +
    marcas.length +
    (origen ? 1 : 0) +
    (motivoPuesto ? 1 : 0) +
    (calculada.desde || calculada.hasta ? 1 : 0) +
    (decidida.desde || decidida.hasta ? 1 : 0) +
    (filtro.sinPrecioPosible ? 1 : 0) +
    (filtro.motivo === 'UMBRAL' ? 1 : 0);
  const verFiltros = filtrosAbiertos ?? filtrosPuestos > 0;

  const hayAvisos =
    hayValores ||
    !!filtro.productId ||
    !!filtro.limitadaPorRegla ||
    !!estrategia ||
    (desactualizadas > 0 && estado === 'PENDING');

  return (
    <div className="space-y-5">
      {/* Los cuatro estados, en píldora (lo único que cambia la pestaña entera). En
          Compite van al encabezado de la página, como pide la maqueta (la tabla queda sola). */}
      {conDestino(
        destinoDelEstado,
        <SegmentosCompite
          aria-label="Estado de las propuestas"
          valor={estado}
          opciones={PESTANAS.map((p) => {
            const cuenta = conteo?.[p.estado];
            // Un cero no se muestra: «Aplicadas solas» sin número se lee mejor que «0».
            return { valor: p.estado, texto: p.texto, cuenta: cuenta ? cuenta : null };
          })}
          onCambiar={(v) => {
            setEstado(v);
            setPagina(1);
            setUltimoLote(null);
            // Los botones del filtro rápido sólo están en Por decidir: sin esto, el
            // filtro seguiría puesto en Aprobadas sin forma de sacarlo.
            setFiltro(SIN_FILTRO);
            // Lote B1: el motivo es sólo de Descartadas.
            setMotivoDescarte(null);
            // Lote B2: la fecha en que se decidió, de una pestaña, no pasa a la otra.
            setDecidida(CUALQUIER_FECHA);
            setCalculada(CUALQUIER_FECHA);
          }}
        />
      )}

      {/* Desglose del último lote */}
      {ultimoLote && (
        <div className="rounded-tarjeta border border-dashed bg-card p-4 text-sm shadow-sm">
          <p className="tracking-tight mb-2 text-sm font-semibold">
            Qué pasó con las {ultimoLote.total}
          </p>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {Object.entries(ultimoLote.porDesenlace).map(([clave, cuantas]) => (
                <Etiqueta
                  key={clave}
                  tono={clave === 'APLICADA' || clave === 'DESCARTADA' ? 'ok' : 'bloqueo'}
                >
                  {textoDesenlace(clave)}: {cuantas}
                </Etiqueta>
              ))}
            </div>
            {ultimoLote.recortadas > 0 && (
              <p className="text-muted-foreground">
                Quedaron {ultimoLote.recortadas} afuera por el límite de este envío. Volvé a
                intentarlo con las que faltan.
              </p>
            )}
            {ultimoLote.resultados
              .filter((r) => r.motivo)
              .slice(0, 10)
              .map((r) => (
                <p key={r.id} className="text-muted-foreground">
                  • {r.motivo}
                </p>
              ))}
          </div>
        </div>
      )}

      <section
        aria-label="Las propuestas"
        className="overflow-clip rounded-tarjeta border bg-card shadow-card"
      >
        {/* 1. Por estrategia (las píldoras de la maqueta) y la búsqueda */}
        <div className="flex flex-wrap items-center gap-2.5 px-5 py-3.5">
          {/* Spec 275 (R1-c): por estrategia, con cuántas tiene cada una (sin el filtro de estrategia). */}
          <ChipsDeEstrategia
            className="w-full min-w-0 sm:w-auto sm:flex-1"
            estrategias={facetas?.estrategias ?? []}
            elegida={estrategia}
            nombreDeLaElegida={nombreDeLaEstrategia}
            totalDeTodas={estrategia === null && facetas ? facetas.total : null}
            onElegir={(id, nombre) => {
              setEstrategia(id);
              setNombreElegido(nombre);
              setPagina(1);
            }}
          />
          {/* Los demás filtros, detrás de un botón: la maqueta deja la tabla sola. Se
              abren solos si ya hay alguno puesto, y el botón dice cuántos. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-expanded={verFiltros}
            aria-controls="filtros-de-la-bandeja"
            onClick={() => setFiltrosAbiertos(!verFiltros)}
            className="h-9 gap-1.5 rounded-lg bg-card px-3 sm:ml-auto"
          >
            <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
            Filtros
            {filtrosPuestos > 0 && (
              <span className="tabular-nums rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                {filtrosPuestos}
              </span>
            )}
          </Button>
          {/* A6: sin botón «Buscar»; Enter busca enseguida, sin esperar. */}
          <form
            role="search"
            className="flex w-full items-center gap-2 sm:w-auto"
            onSubmit={(e) => {
              e.preventDefault();
              setBusquedaAplicada(busqueda.trim());
              setPagina(1);
            }}
          >
            <div className="relative w-full sm:w-auto">
              <Search
                aria-hidden="true"
                className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por producto o código"
                aria-label="Buscar por producto o código"
                className="h-9 w-full rounded-lg bg-campo pl-10 text-sm sm:w-56"
              />
            </div>
          </form>
        </div>

        {/* 2. Los demás filtros, en una franja más chica: no compiten con la tabla. */}
        {verFiltros && (
          <div
            id="filtros-de-la-bandeja"
            // Todos los disparadores del mismo alto, en píldora: la franja se lee como una sola cosa.
            className="flex flex-wrap items-center gap-2 border-y bg-muted/30 px-5 py-2.5 [&>button]:h-8 [&>button]:rounded-full [&>button]:bg-card [&>button]:text-xs [&>select]:h-8 [&>select]:rounded-full [&>select]:text-xs"
            data-testid="filtros-de-la-bandeja"
          >
            <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              <SlidersHorizontal aria-hidden="true" className="h-3.5 w-3.5" />
              Filtrar
            </span>
            {/* Lote B1: sólo los valores que hay en la bandeja, con cuántas. */}
            <FiltroDeValores
              titulo="Categoría"
              sinValor="Sin categoría"
              buscarUna="Buscar una categoría"
              valores={facetas?.categorias ?? null}
              elegidos={categorias}
              onCambiar={cambiarCategorias}
              pie={
                facetas
                  ? `${cantidadDe(facetas.categorias.filter((c) => c.valor != null).length, 'categoría', 'categorías')} ${estado === 'PENDING' ? 'con algo por decidir' : 'en esta pestaña'}, ${facetas.categoriasDelCatalogo === 1 ? 'la única' : `de las ${formatoCantidad(facetas.categoriasDelCatalogo)}`} de tu catálogo.`
                  : ''
              }
            />
            <FiltroDeValores
              titulo="Marca"
              sinValor="Sin marca"
              buscarUna="Buscar una marca"
              valores={facetas?.marcas ?? null}
              elegidos={marcas}
              onCambiar={cambiarMarcas}
              pie="Se puede elegir más de una."
            />
            {/* Lote C1 (punto 8): de dónde viene, con cuántas hay de cada origen. */}
            <SelectNativo
              aria-label="Origen"
              className="h-8 bg-card"
              value={origen ?? ''}
              onChange={(e) => {
                setOrigen((e.target.value || null) as OrigenDeLaPropuesta | null);
                setPagina(1);
              }}
            >
              <option value="">Origen: todos</option>
              {OPCIONES_DE_ORIGEN.map((o) => {
                const n = facetas?.origenes?.find((x) => x.valor === o.valor)?.cuantas ?? 0;
                return (
                  <option key={o.valor} value={o.valor} disabled={n === 0 && origen !== o.valor}>
                    {`${o.texto} (${formatoCantidad(n)})`}
                  </option>
                );
              })}
            </SelectNativo>
            {/* Spec 275 (R2, D-R2-9): cuándo se calculó, con el mismo calendario. */}
            {estado === 'PENDING' && (
              <SelectorDeFechas
                etiqueta="Calculada"
                valor={calculada}
                onCambiar={(r) => {
                  setCalculada(r);
                  setPagina(1);
                }}
              />
            )}
            {/* Lote B2 (punto 7): cuándo se decidió, con el calendario (incluye «Descartada: cualquier fecha»). */}
            {seDecidio && (
              <SelectorDeFechas
                etiqueta={
                  estado === 'DISCARDED'
                    ? 'Descartada'
                    : estado === 'AUTO_APPLIED'
                      ? 'Aplicada'
                      : 'Aprobada'
                }
                valor={decidida}
                onCambiar={(r) => {
                  setDecidida(r);
                  setPagina(1);
                }}
              />
            )}
            {/* Lote B1 (D2): Descartadas por motivo, con cuántas hay de cada uno. */}
            {estado === 'DISCARDED' && (
              <SelectNativo
                aria-label="Motivo"
                className="h-8 bg-card"
                value={motivoDescarte ?? ''}
                onChange={(e) => {
                  setMotivoDescarte((e.target.value || null) as FiltroDeMotivo | null);
                  setPagina(1);
                }}
              >
                <option value="">Motivo: todos</option>
                {OPCIONES_DE_MOTIVO.map((o) => {
                  const n = facetas?.motivos?.find((m) => m.valor === o.valor)?.cuantas ?? 0;
                  if (n === 0 && motivoDescarte !== o.valor) return null;
                  return (
                    <option key={o.valor} value={o.valor}>
                      {`${o.texto} (${formatoCantidad(n)})`}
                    </option>
                  );
                })}
              </SelectNativo>
            )}
            {/* Sólo las sin precio posible / sólo los cambios grandes (desde Decisiones, o apretando) */}
            {estado === 'PENDING' && (
              <>
                <span aria-hidden="true" className="mx-1 hidden h-5 w-px bg-border sm:block" />
                <BotonRapido
                  puesto={!!filtro.sinPrecioPosible}
                  onClick={() => {
                    setFiltro(filtro.sinPrecioPosible ? SIN_FILTRO : { sinPrecioPosible: true });
                    setPagina(1);
                  }}
                >
                  Sólo con un conflicto entre reglas
                </BotonRapido>
                <BotonRapido
                  puesto={filtro.motivo === 'UMBRAL'}
                  onClick={() => {
                    setFiltro(filtro.motivo === 'UMBRAL' ? SIN_FILTRO : { motivo: 'UMBRAL' });
                    setPagina(1);
                  }}
                >
                  Sólo cambios grandes
                </BotonRapido>
              </>
            )}
          </div>
        )}

        {/* 3. Lo que está recortando la lista, dicho una vez y con cómo sacarlo. */}
        {hayAvisos && (
          <div className="space-y-2 border-b px-5 py-3 text-sm">
            {/* Lote B1: los filtros puestos, cada uno con su ×. */}
            {hayValores && (
              <div className="flex flex-wrap items-center gap-2" data-testid="filtros-puestos">
                <span className="text-muted-foreground">
                  Mostrando {formatoCantidad(total)}
                  {facetas ? ` de ${formatoCantidad(facetas.total)}` : ''}
                </span>
                {categorias.map((c) => (
                  <ChipDeFiltro
                    key={`c-${c}`}
                    texto={`Categoría: ${c === '' ? 'sin categoría' : c}`}
                    onSacar={() => cambiarCategorias(categorias.filter((x) => x !== c))}
                  />
                ))}
                {marcas.map((m) => (
                  <ChipDeFiltro
                    key={`m-${m}`}
                    texto={`Marca: ${m === '' ? 'sin marca' : m}`}
                    onSacar={() => cambiarMarcas(marcas.filter((x) => x !== m))}
                  />
                ))}
                {origen && (
                  <ChipDeFiltro
                    texto={`Origen: ${OPCIONES_DE_ORIGEN.find((o) => o.valor === origen)!.texto.toLowerCase()}`}
                    onSacar={() => {
                      setOrigen(null);
                      setPagina(1);
                    }}
                  />
                )}
                {motivoPuesto && (
                  <ChipDeFiltro
                    texto={`Motivo: ${OPCIONES_DE_MOTIVO.find((o) => o.valor === motivoPuesto)?.texto ?? motivoPuesto}`}
                    onSacar={() => {
                      setMotivoDescarte(null);
                      setPagina(1);
                    }}
                  />
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 rounded-full"
                  onClick={sacarLosValores}
                >
                  Sacar los filtros
                </Button>
              </div>
            )}

            {/* Lote C2b: se llegó a UN producto (desde su ficha o los productos de una estrategia). */}
            {filtro.productId && (
              <div className="flex items-center gap-2">
                <span>
                  {filas[0] ? `Sólo «${filas[0].productName}»` : 'Sólo el producto que elegiste'}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 rounded-full"
                  onClick={() => setFiltro(SIN_FILTRO)}
                >
                  Ver todas
                </Button>
              </div>
            )}

            {/* «Ver cuáles» de lo que cuidaron las reglas (Decisiones, 4B-datos T4) */}
            {filtro.limitadaPorRegla && (
              // Rediseño (15-oct): se llega acá desde «Ver los que esperan» de Decisiones.
              // El título dice todo lo que espera; este cartel tiene que verse a la
              // primera para que se entienda que la lista está recortada, y cuántos son.
              <div
                role="status"
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-primary/10 px-3 py-2"
              >
                <ShieldCheck aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
                <span className="font-semibold text-foreground">
                  Las que una regla cuidó
                  {filtro.dias ? ` en los últimos ${filtro.dias} días` : ''}
                </span>
                {!cargando && (
                  <span className="tabular-nums font-semibold text-primary">
                    {formatoCantidad(total)} {total === 1 ? 'precio' : 'precios'}
                  </span>
                )}
                {/* Decisiones parte lo que cuidaron por dónde quedó (28-sep): se llega a una
                    pestaña o a la otra, y el cartel dice cuál es. */}
                <span className="text-muted-foreground">
                  {estado === 'PENDING'
                    ? 'Sólo las que esperan tu decisión; las que se aplicaron solas tienen su pestaña.'
                    : estado === 'AUTO_APPLIED'
                      ? 'Sólo las que se aplicaron solas; las que esperan están en Por decidir.'
                      : null}
                </span>
                {!vacioFiltrado && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto h-7 rounded-full bg-card"
                    onClick={() => {
                      setFiltro(SIN_FILTRO);
                      setPagina(1);
                    }}
                  >
                    Quitar el filtro
                  </Button>
                )}
              </div>
            )}

            {/* Se entró por una decisión: se dice que la lista está recortada */}
            {estrategia && (
              <div className="flex min-w-0 items-center gap-2">
                {/* Ronda del QA de R4 (R4-46): un nombre largo se corta; entero en el title. */}
                <span className="min-w-0 truncate text-muted-foreground" title={avisoDelFiltro}>
                  {avisoDelFiltro}
                </span>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto shrink-0 p-0"
                  onClick={() => {
                    setEstrategia(null);
                    setNombreElegido(null);
                  }}
                >
                  Ver todas
                </Button>
              </div>
            )}

            {/* Aviso: hay propuestas que ya no aplican */}
            {desactualizadas > 0 && estado === 'PENDING' && (
              <Aviso
                tono="alerta"
                className="py-2.5 text-sm"
                titulo={`${desactualizadas} propuesta${desactualizadas === 1 ? '' : 's'} de esta página ya no aplica${desactualizadas === 1 ? '' : 'n'}`}
              >
                <p>
                  {cuantas.VUELTA === 0
                    ? 'El precio del producto cambió'
                    : 'El precio del producto (o, en una vuelta, su costo) cambió'}{' '}
                  por otro camino después de que se calculó la propuesta, así que el valor propuesto
                  quedó viejo. {avisoDeLasViejas(cuantas)}
                </p>
              </Aviso>
            )}
          </div>
        )}

        {/* 4. La tabla */}
        {cargando ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <div className="p-5">
            <EstadoError onReintentar={() => void cargar()}>{error}</EstadoError>
          </div>
        ) : vacioDeValores ? (
          <div className="p-5">
            <EstadoVacio
              icono={Inbox}
              titulo={vacioDeValores.titulo}
              accion={
                <Button variant="outline" size="sm" onClick={sacarLosValores}>
                  Sacar los filtros
                </Button>
              }
            >
              {vacioDeValores.detalle}
            </EstadoVacio>
          </div>
        ) : filas.length === 0 ? (
          <div className="p-5">
            <Vacio
              estado={estado}
              conBusqueda={!!busquedaAplicada}
              conFiltro={hayFiltro}
              onQuitarFiltro={quitarFiltros}
            />
          </div>
        ) : (
          <>
            <Table className="[&_td]:py-3.5">
              <TableHeader className="bg-muted/50 [&_th]:h-[42px] [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground">
                <TableRow className="hover:bg-transparent">
                  {estado === 'PENDING' && (
                    <TableHead className="w-12 pl-5">
                      <Checkbox
                        checked={todasElegidas}
                        onCheckedChange={alternarTodas}
                        disabled={seleccionables.length === 0}
                        aria-label="Elegir todas las que se pueden decidir"
                      />
                    </TableHead>
                  )}
                  {estado === 'DISCARDED' ? (
                    // Lote B1 (D2, D10): quién, cuándo (en hora de Bolivia, dicho una vez) y por qué.
                    <>
                      {/* Ola final R1: la columna tiene ancho tope (la tabla no
                          es table-fixed): un nombre largo corta en su celda. */}
                      <TableHead className="w-72 pl-5">Producto</TableHead>
                      <TableHead className="whitespace-nowrap text-right">Propuesto (Bs)</TableHead>
                      <TableHead className="text-right">Cambio</TableHead>
                      <TableHead>Quién</TableHead>
                      <TableHead className="whitespace-nowrap">
                        Cuándo <span className="font-normal">· hora de Bolivia</span>
                      </TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>
                        <span className="sr-only">Acciones</span>
                      </TableHead>
                    </>
                  ) : estado === 'PENDING' ? (
                    <>
                      <TableHead className="w-72">Producto</TableHead>
                      {/* «Bs» va en la caja del sugerido (maqueta); el lector lo sigue oyendo. */}
                      <TableHead className="text-right">
                        Actual <span className="sr-only">(Bs)</span>
                      </TableHead>
                      <TableHead className="text-right">
                        Sugerido <span className="sr-only">(Bs)</span>
                      </TableHead>
                      <TableHead>Competencia</TableHead>
                      <TableHead>Margen</TableHead>
                      <TableHead className="text-right">
                        <span className="sr-only">Acciones</span>
                      </TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="w-72 pl-5">Producto</TableHead>
                      <TableHead className="text-right">Antes (Bs)</TableHead>
                      <TableHead className="text-right">Aplicado (Bs)</TableHead>
                      <TableHead>Margen</TableHead>
                      {/* Lote B2 (D10): en Aprobadas y Aplicadas solas, la hora de quién decidió. */}
                      <TableHead>
                        Por qué <span className="font-normal">· hora de Bolivia</span>
                      </TableHead>
                      <TableHead className="text-right">
                        <span className="sr-only">Acciones</span>
                      </TableHead>
                    </>
                  )}
                  <TableHead className="w-12 pr-5">
                    <span className="sr-only">Abrir</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.map((f) => {
                  if (estado === 'DISCARDED') {
                    return filaQueSeAbre(
                      f,
                      <CeldasDeDescartada
                        f={f}
                        trabajando={trabajando}
                        onVolver={(id) => void devolver([id])}
                      />
                    );
                  }
                  // El costo cambiado (Task 0, ajuste 1) se trata igual que el precio cambiado.
                  const bloqueada =
                    (f.desactualizada || !!f.costoCambio || !!f.yaNoAplica) &&
                    f.status === 'PENDING';
                  const aElegir = !!f.sinPrecioPosible && f.status === 'PENDING';
                  const unaPorUna = vaUnaPorUna(f);
                  const pendiente = estado === 'PENDING';
                  return filaQueSeAbre(
                    f,
                    <>
                      {pendiente && (
                        <TableCell className="pl-5 align-middle">
                          <Checkbox
                            checked={seleccion.has(f.id)}
                            onCheckedChange={() => alternarUna(f.id)}
                            disabled={bloqueada || aElegir || unaPorUna}
                            aria-label={
                              aElegir
                                ? `${f.productName}: conflicto entre reglas, se decide una por una`
                                : unaPorUna && f.debajoDelCosto
                                  ? `${f.productName}: queda debajo del costo, se aprueba una por una`
                                  : unaPorUna && f.vieja
                                    ? `${f.productName}: se calculó ${haceDias(f)} y el mercado cambió, se aprueba una por una`
                                    : unaPorUna
                                      ? `${f.productName}: usó el precio de una tienda que ya no está entre las tuyas, se aprueba una por una`
                                      : `Elegir ${f.productName}`
                            }
                          />
                        </TableCell>
                      )}

                      {/* El producto, de qué estrategia viene (su punto de color) y,
                          debajo, lo que hay que saber antes de decidir. */}
                      <TableCell
                        className={cn(
                          'min-w-0 max-w-[24rem] align-middle [overflow-wrap:anywhere]',
                          !pendiente && 'pl-5'
                        )}
                      >
                        <CeldaProducto
                          nombre={
                            <span
                              className="line-clamp-1 font-semibold text-foreground [overflow-wrap:anywhere]"
                              title={f.productName}
                            >
                              {f.productName}
                            </span>
                          }
                          detalle={[f.sku, f.category, f.brand].filter(Boolean).join(' · ')}
                        >
                          <LineaDeEstrategia
                            f={f}
                            color={colorDeEstrategia(f.strategyApplicationId, facetas?.estrategias)}
                            pendiente={pendiente}
                          />
                          <div className="space-y-0.5 text-xs">
                            {/* Lote C1 (punto 8): por qué la propone el recálculo, congelado.
                                Ronda 2 C1: «·» y, la que reemplaza a una que esperaba, dicho
                                así (porQueEnTexto lo explica para la vista previa).
                                Spec 294 (D-R4-3): la que propuso su estrategia y reescribió
                                un recálculo no dice «Recálculo»: dice cuándo la recalculó. */}
                            {f.recalculadaEl ? (
                              <div className="text-muted-foreground">
                                Recalculada el {cuando(f.recalculadaEl)}
                                {f.porQueDelRecalculo &&
                                  ` · ${
                                    f.porQueDelRecalculo.tipo === 'YA_ESPERABA'
                                      ? 'reemplazó la que esperaba'
                                      : porQueEnTexto(f.porQueDelRecalculo)
                                  }`}
                              </div>
                            ) : (
                              f.porQueDelRecalculo && (
                                <div className="text-muted-foreground">
                                  Recálculo ·{' '}
                                  {f.porQueDelRecalculo.tipo === 'YA_ESPERABA'
                                    ? 'reemplazó la que esperaba'
                                    : porQueEnTexto(f.porQueDelRecalculo)}
                                </div>
                              )
                            )}
                            {f.triggerKind === 'EXPIRY' && (
                              <div className="text-alerta">
                                Vuelve a su precio: la estrategia venció
                              </div>
                            )}
                            {/* Lote C1 (punto 9): aplica sola, pero el costo no la dejó. */}
                            {f.status === 'PENDING' && f.motivoPendiente === 'BAJO_COSTO' && (
                              <div className="text-bloqueo">
                                No se aplicó sola: quedaba debajo del costo.
                              </div>
                            )}
                            {f.status === 'PENDING' && f.motivoPendiente === 'SIN_COSTO' && (
                              <div className="text-alerta">
                                No se aplicó sola: no tiene costo, te pregunta.
                              </div>
                            )}
                            {/* P2-D6: a una vuelta bloqueada no le llega ninguna
                                «próxima corrida»: su estrategia está cerrada. */}
                            {f.triggerKind === 'EXPIRY' &&
                              bloqueada &&
                              f.strategyApplicationId &&
                              onAbrirVuelta && (
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-alerta">
                                  <span>
                                    El precio (o el costo) cambió desde que se propuso: no vuelve
                                    sola.
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 rounded-full"
                                    onClick={() => onAbrirVuelta(f.strategyApplicationId!)}
                                  >
                                    Dejarla desde la vuelta
                                  </Button>
                                </div>
                              )}
                            {f.costoCambio &&
                              f.status === 'PENDING' &&
                              f.triggerKind !== 'EXPIRY' && (
                                <div className="text-alerta">
                                  El costo cambió desde que se propuso: {queLePasaEnFrase(f)}
                                </div>
                              )}
                            {/* S3 (8-oct): siempre, aunque sea reciente (no espera los 15 días de la vieja). */}
                            {f.avisoTiendaQuitada && f.status === 'PENDING' && (
                              <div className="text-alerta">{f.avisoTiendaQuitada}</div>
                            )}
                          </div>
                          {/* Las marcas que cambian cómo se decide: van juntas, abajo. */}
                          {((f.desactualizada && f.status === 'PENDING') ||
                            aElegir ||
                            (unaPorUna && (f.debajoDelCosto || f.vieja))) && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              {/* Sólo el precio cambiado: el costo cambiado tiene su propio aviso. */}
                              {f.desactualizada && f.status === 'PENDING' && (
                                <Etiqueta tono="alerta">
                                  Ya no aplica: el precio cambió a {plata(f.precioActual)}
                                </Etiqueta>
                              )}
                              {aElegir && (
                                <Etiqueta tono="bloqueo">Conflicto entre reglas</Etiqueta>
                              )}
                              {unaPorUna && f.debajoDelCosto && f.perdidaPorUnidad != null && (
                                <Etiqueta tono="bloqueo">
                                  {`Debajo del costo: perdés ${plata(f.perdidaPorUnidad)} por unidad`}
                                </Etiqueta>
                              )}
                              {/* Auditoría I3: una sin precio posible también dice su antigüedad. */}
                              {(unaPorUna || aElegir) && f.vieja && (
                                <Etiqueta tono="alerta">{`De ${haceDias(f)}: el mercado cambió`}</Etiqueta>
                              )}
                            </div>
                          )}
                        </CeldaProducto>
                      </TableCell>

                      <TableCell className="align-middle text-right tabular-nums text-foreground/80">
                        <EnlaceAProducto href={f.productUrl} tienda="tu tienda">
                          {plata(f.priceBefore)}
                        </EnlaceAProducto>
                      </TableCell>
                      <TableCell className="align-middle text-right">
                        {aElegir ? (
                          <span className="text-muted-foreground">a elegir</span>
                        ) : (
                          <PrecioSugerido f={f} />
                        )}
                      </TableCell>

                      {pendiente ? (
                        <>
                          <TableCell className="align-middle">
                            <CeldaCompetencia f={f} aElegir={aElegir} />
                          </TableCell>
                          <TableCell className="min-w-0 max-w-[16rem] align-middle [overflow-wrap:anywhere]">
                            <CeldaMargen f={f} aElegir={aElegir} />
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex flex-nowrap items-center justify-end gap-2 whitespace-nowrap">
                              {bloqueada ? null : aElegir ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full"
                                  onClick={() => setDecidiendo(f)}
                                >
                                  Decidir
                                </Button>
                              ) : abierta === f.id ? null : unaPorUna ? (
                                // Lote A del 2-oct (A1): no va en el lote; se aprueba mirándola.
                                // Tarea 8: con la fila abierta ya están abajo, en el detalle.
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-full"
                                    disabled={trabajando}
                                    onClick={() => pedirAprobarUna(f)}
                                    {...sinCambios}
                                  >
                                    Aprobar esta
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="rounded-full"
                                    disabled={trabajando}
                                    onClick={() => setADescartar({ ids: [f.id], fila: f })}
                                    {...sinCambios}
                                  >
                                    Descartar
                                  </Button>
                                </>
                              ) : (
                                // La maqueta: aprobar o descartar desde la fila, sin abrirla.
                                // Aprobar pregunta antes, como el «Aprobar X» del detalle.
                                <>
                                  <BotonRedondo
                                    tono="ok"
                                    etiqueta={`Aprobar ${plata(f.priceAfter)} para ${f.productName}`}
                                    disabled={trabajando}
                                    onClick={() => pedirAprobarDesdeLaFila(f)}
                                  >
                                    <Check className="h-4 w-4" strokeWidth={2.5} />
                                  </BotonRedondo>
                                  <BotonRedondo
                                    tono="baja"
                                    etiqueta={`Descartar ${f.productName}`}
                                    disabled={trabajando}
                                    onClick={() => setADescartar({ ids: [f.id], fila: f })}
                                  >
                                    <X className="h-4 w-4" strokeWidth={2.5} />
                                  </BotonRedondo>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="min-w-0 max-w-[16rem] align-middle [overflow-wrap:anywhere]">
                            <CeldaMargen f={f} aElegir={false} />
                          </TableCell>
                          <TableCell className="min-w-0 align-middle text-sm [overflow-wrap:anywhere]">
                            {f.status !== 'PENDING' && f.salidaElegida && (
                              <div className="text-xs text-muted-foreground">
                                Se eligió:{' '}
                                {minusculaInicial(tituloDeSalida(f.salidaElegida, f.rangos))}
                              </div>
                            )}
                            {f.status !== 'PENDING' && quienDecidio(f) && (
                              <div className="text-xs text-muted-foreground">{quienDecidio(f)}</div>
                            )}
                          </TableCell>
                          <TableCell className="align-middle text-right">
                            {/* Tarea 7 (y DO-3): deshacer una aprobada o una aplicada sola. */}
                            {isAdmin &&
                              (f.status === 'APPLIED' || f.status === 'AUTO_APPLIED') &&
                              (f.deshacer ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full"
                                  disabled={trabajando}
                                  aria-label={`Volver al precio de antes de ${f.productName} (${formatoPrecio(f.deshacer.precioDeAntes)})`}
                                  onClick={() => setADeshacer(f)}
                                  {...sinCambios}
                                >
                                  <Undo2 aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
                                  Volver al precio de antes
                                </Button>
                              ) : (
                                f.sinDeshacer &&
                                SIN_DESHACER_EN_TEXTO[f.sinDeshacer] && (
                                  // Ronda 1: la explicación larga, al pasar el mouse y
                                  // también para un lector de pantalla (sr-only).
                                  <div
                                    className="text-xs text-muted-foreground"
                                    title={SIN_DESHACER_DETALLE[f.sinDeshacer]}
                                  >
                                    <span>{SIN_DESHACER_EN_TEXTO[f.sinDeshacer]}</span>
                                    <span className="sr-only">
                                      {`: ${SIN_DESHACER_DETALLE[f.sinDeshacer]}`}
                                    </span>
                                  </div>
                                )
                              ))}
                          </TableCell>
                        </>
                      )}
                    </>,
                    bloqueada ? 'opacity-60' : undefined
                  );
                })}
              </TableBody>
            </Table>

            {/* 5. El pie: la barra del lote (flota abajo mientras se baja; al final de
                la tabla queda en su lugar) y la paginación. */}
            <BarraDeLote
              visible={estado === 'PENDING' && seleccion.size > 0}
              className="mx-4 mb-3 mt-3 w-auto max-w-none"
              resumen={
                <p>
                  <b className="font-semibold">
                    {seleccion.size} elegida{seleccion.size === 1 ? '' : 's'}
                  </b>
                  {resumenDelLote && (
                    <span className="text-foreground/80"> · {resumenDelLote}</span>
                  )}
                </p>
              }
              acciones={
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-full"
                    onClick={() => setSeleccion(new Set())}
                  >
                    Limpiar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => pedirConfirmacion('descartar')}
                    disabled={trabajando}
                    {...sinCambios}
                    className="h-9 gap-1.5 rounded-full border-baja-borde bg-card px-4 text-baja hover:bg-baja-suave hover:text-baja"
                  >
                    <X className="h-4 w-4" />
                    Descartar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => pedirConfirmacion('aprobar')}
                    disabled={trabajando}
                    {...sinCambios}
                    className={cn('h-9 gap-1.5 rounded-full px-5', BOTON_DEGRADADO)}
                  >
                    {trabajando ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    Aprobar
                  </Button>
                </>
              }
            />
            <div className="border-t px-5 py-3">
              <Pagination
                page={pagina}
                totalPages={totalPaginas}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPagina}
                disabled={trabajando}
                itemName="propuestas"
              />
            </div>
          </>
        )}
      </section>

      <Sheet open={decidiendo !== null} onOpenChange={(v) => !v && setDecidiendo(null)}>
        <SheetContent className="w-[920px] overflow-y-auto sm:max-w-[920px]">
          <SheetHeader>
            <SheetTitle>Decidir un precio sin salida que cumpla todo</SheetTitle>
          </SheetHeader>
          {decidiendo && (
            <FilaSinPrecio
              fila={decidiendo}
              onResuelta={() => {
                setDecidiendo(null);
                void recargar();
              }}
            />
          )}
        </SheetContent>
      </Sheet>
      <FichaEstrategiaDialog estrategia={fichaDe} onCerrar={() => setFichaDe(null)} />
      <ProductHistoryDialog
        open={historialDe !== null}
        onOpenChange={(v) => !v && setHistorialDe(null)}
        productId={historialDe?.productId ?? null}
        productName={historialDe?.productName ?? ''}
        // Ronda 1 (I2): volver o deshacer desde el historial cambia el precio y
        // puede dejar o resolver propuestas: la bandeja se trae otra vez.
        onVolvio={() => void recargar()}
      />
      {/* Tarea 7: la misma ventana del historial, en modo deshacer. Se monta
          sólo con fila y con su `key`: otra fila nunca ve la revisión de la anterior. */}
      {aDeshacer?.deshacer && (
        <VolverAEstePrecioDialog
          key={aDeshacer.id}
          productId={aDeshacer.productId}
          productName={aDeshacer.productName}
          // Sin `despues`: al deshacer la ventana sólo usa el precio de antes, y
          // la fila no trae el precio que dejó el cambio (no es el `priceAfter`
          // de la propuesta si se eligió otra salida).
          cambio={{
            cambioId: aDeshacer.deshacer.cambioId,
            antes: aDeshacer.deshacer.precioDeAntes,
          }}
          alPrecioDeAntes
          onCerrar={() => {
            setADeshacer(null);
            // Ronda 1: un 409/422 dice que la fila quedó vieja; se trae otra vez.
            void recargar();
          }}
          onListo={(r, usadoElSugerido) => {
            setADeshacer(null);
            toast({
              // Ronda 1: con «Usar Z» quedó en Z, no volvió a X.
              title: `${usadoElSugerido ? 'Quedó en' : 'Volvió a'} ${formatoPrecio(r.precio)}`,
              // El título ya dice el precio: la descripción, sólo lo demás (ronda 5-oct).
              description: loDemasDelAviso(r) ?? undefined,
            });
            void recargar();
          }}
        />
      )}
      <DialogoDescartar
        abierto={aDescartar !== null}
        cuantas={aDescartar?.ids.length ?? 0}
        onCerrar={() => setADescartar(null)}
        onDescartar={async (motivo) => {
          const que = aDescartar;
          if (!que) return;
          if (que.fila) await descartarUna(que.fila, motivo);
          else await correrLote('descartar', motivo);
          setADescartar(null);
        }}
      />
      <ConfirmDialog />
    </div>
  );
}

/**
 * El margen bruto sobre el precio, como el servidor (`margenBrutoPct`): sin
 * costo (o con un costo 0) o sin precio, null — no se inventa.
 */
export function margenDe(precio: number, costo: number | null | undefined): number | null {
  if (costo == null || !Number.isFinite(costo) || !(costo > 0)) return null;
  if (!(precio > 0)) return null;
  return ((precio - costo) / precio) * 100;
}

/**
 * El costo que muestra la fila. Ronda final: el congelado puede faltar (se
 * borró después de proponer) y se muestra el de hoy. Ronda del QA de R4: con
 * el costo cambiado, el de hoy (el mismo que usa el detalle), no el de entonces.
 */
const costoVisto = (f: PriceProposalRow): number | null =>
  f.costoCambio && f.status === 'PENDING' && f.costoHoy != null
    ? f.costoHoy
    : (f.costo ?? f.costoHoy ?? null);

const promedio = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;

/**
 * La barra del lote de la maqueta: «suben en promedio 20,0 % · margen promedio
 * 27,0 %». Con lo que trae cada fila: las que no tienen costo no entran al
 * margen (y se dice cuántas son). null = no hay nada que decir.
 */
export function resumenDeLasElegidas(elegidas: PriceProposalRow[]): string | null {
  const partes: string[] = [];
  const cambios = elegidas
    .map((f) => f.diferenciaPct)
    .filter((p): p is number => p != null && Number.isFinite(p));
  if (cambios.length > 0) {
    const p = promedio(cambios);
    partes.push(
      Math.abs(p) < 0.05
        ? 'quedan igual en promedio'
        : p > 0
          ? `suben en promedio ${formatoPorcentaje(p)}`
          : `bajan en promedio ${formatoPorcentaje(Math.abs(p))}`
    );
  }
  const margenes = elegidas
    .map((f) => margenDe(f.priceAfter, costoVisto(f)))
    .filter((m): m is number => m != null);
  if (margenes.length > 0) {
    const sinCosto = elegidas.length - margenes.length;
    partes.push(
      `margen promedio ${formatoPorcentaje(promedio(margenes))}${sinCosto > 0 ? ` (${formatoCantidad(sinCosto)} sin costo)` : ''}`
    );
  }
  return partes.length > 0 ? partes.join(' · ') : null;
}

/** «Calculada hoy» / «hace 1 día» / «hace 12 días» (spec 275, R2: no vence, sólo se dice). */
const calculadaHace = (dias: number) =>
  dias === 0
    ? 'Calculada hoy'
    : dias === 1
      ? 'Calculada hace 1 día'
      : `Calculada hace ${formatoCantidad(dias)} días`;

/**
 * Debajo del producto, de dónde viene (la maqueta: el punto de color de su
 * estrategia y el nombre) y, en Por decidir, cuándo se calculó.
 */
function LineaDeEstrategia({
  f,
  color,
  pendiente,
}: {
  f: PriceProposalRow;
  color: string;
  pendiente: boolean;
}) {
  const nombre =
    f.strategyName ??
    (f.origenDelPedido === 'HOY' ? 'Sin estrategia' : 'Sin estrategia registrada');
  return (
    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
      <span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-full', color)} />
      <span className="min-w-0 font-medium text-foreground/75">{nombre}</span>
      {pendiente && f.diasDesdeElCalculo != null && (
        <>
          <span aria-hidden="true">·</span>
          <span>{calculadaHace(f.diasDesdeElCalculo)}</span>
        </>
      )}
    </div>
  );
}

/**
 * El precio sugerido en su caja (la maqueta): el borde dice hacia dónde va —
 * verde si sube, rojo si baja, como `CambioDePrecio` —, y abajo cuánto.
 */
function PrecioSugerido({ f }: { f: PriceProposalRow }) {
  const sentido = sentidoDe(f.diferencia);
  return (
    <div className="inline-flex flex-col items-end gap-1">
      <span
        className={cn(
          'tabular-nums inline-flex h-9 min-w-[7.5rem] items-center justify-between gap-3 rounded-lg border-[1.5px] bg-card px-2.5',
          sentido === 'SUBE'
            ? 'border-ok-borde'
            : sentido === 'BAJA'
              ? 'border-baja-borde'
              : 'border-border'
        )}
      >
        <span aria-hidden="true" className="text-xs text-muted-foreground">
          Bs
        </span>
        <span className="text-sm font-extrabold text-foreground">{plata(f.priceAfter)}</span>
      </span>
      <CambioDePrecio diferencia={f.diferencia} porcentaje={f.diferenciaPct} className="text-xs" />
    </div>
  );
}

/**
 * Contra quién se mide: la tienda que usó la estrategia (o, sin ella, el más
 * barato del mercado) y a qué distancia del más barato queda lo sugerido. Una
 * «a elegir» no la dice: todavía no tiene precio (revisión final, minor 8).
 */
function CeldaCompetencia({ f, aElegir }: { f: PriceProposalRow; aElegir: boolean }) {
  const precio = f.competitorPrice ?? f.precioMasBarato ?? null;
  const quien =
    f.competitorPrice != null || f.competitorStoreName
      ? f.competitorStoreName
      : precio != null
        ? 'el más barato'
        : null;
  const distancia =
    !aElegir && f.distanciaAlMasBaratoPct != null
      ? distanciaAlMasBarato(f.distanciaAlMasBaratoPct)
      : null;
  if (precio == null && !quien && !distancia)
    return <span className="text-muted-foreground">—</span>;
  return (
    <div className="min-w-0 space-y-0.5">
      {precio != null && (
        <div className="tabular-nums font-semibold">
          <EnlaceAProducto href={f.competitorUrl} tienda={quien}>
            <span className="text-xs font-normal text-muted-foreground">Bs </span>
            {plata(precio)}
          </EnlaceAProducto>
        </div>
      )}
      {quien && (
        <div className="text-xs text-muted-foreground [overflow-wrap:anywhere]">{quien}</div>
      )}
      {distancia && <div className="text-xs text-muted-foreground">Sugerido: {distancia}</div>}
    </div>
  );
}

/**
 * El margen de hoy y el que queda (la maqueta: «12% → 27%»), con el costo que
 * se usó. Si una regla lo frenó, lo dice abajo (el «tope de la regla»).
 */
function CeldaMargen({ f, aElegir }: { f: PriceProposalRow; aElegir: boolean }) {
  const costo = costoVisto(f);
  const antes = margenDe(f.priceBefore, costo);
  const despues = aElegir ? null : margenDe(f.priceAfter, costo);
  const tono =
    despues == null
      ? 'text-muted-foreground'
      : despues < 0
        ? 'text-bloqueo'
        : antes != null && despues > antes + 0.05
          ? 'text-ok'
          : antes != null && despues < antes - 0.05
            ? 'text-baja'
            : 'text-foreground';
  return (
    <div className="min-w-0 space-y-1">
      {costo == null ? (
        <div className="text-xs text-muted-foreground">Sin costo</div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 whitespace-nowrap font-semibold tabular-nums">
            <span className="text-foreground/75">
              {antes != null ? formatoPorcentaje(antes) : '—'}
            </span>
            <ArrowRight aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="sr-only">pasa a</span>
            <span className={tono}>{despues != null ? formatoPorcentaje(despues) : '—'}</span>
          </div>
          <div className="text-xs text-muted-foreground">costo {plata(costo)}</div>
        </>
      )}
      {!aElegir && f.heldByRuleName && (
        // El «tope de la regla» de la maqueta, en el ámbar de siempre: una regla lo frenó.
        <Etiqueta
          tono="alerta"
          className="max-w-full items-start whitespace-normal rounded-full px-2 py-1 leading-tight"
        >
          <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 [overflow-wrap:anywhere]">
            Lo limita «{f.heldByRuleName}»
            {(f.limitantes?.length ?? 0) > 1 && ` y ${f.limitantes!.length - 1} más`}
          </span>
        </Etiqueta>
      )}
    </div>
  );
}

/** Aprobar (relleno, verde) o descartar (con borde, rojo) desde la fila: los redondos de la maqueta. */
function BotonRedondo({
  tono,
  etiqueta,
  disabled,
  onClick,
  children,
}: {
  tono: 'ok' | 'baja';
  etiqueta: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  // Soporte («Entrar como») en una demo sólo mira.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());
  return (
    <button
      type="button"
      aria-label={etiqueta}
      title={etiqueta}
      disabled={disabled}
      {...sinCambios}
      onClick={onClick}
      className={cn(
        'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors active:scale-95',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        tono === 'ok'
          ? 'bg-ok text-background shadow-sm hover:brightness-110'
          : 'border-[1.5px] border-baja-borde bg-card text-baja hover:bg-baja-suave'
      )}
    >
      {children}
    </button>
  );
}

/** Un filtro rápido que se prende y se apaga, en píldora. */
function BotonRapido({
  puesto,
  onClick,
  children,
}: {
  puesto: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={puesto}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center rounded-full border px-3 text-xs font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        puesto ? PUESTO : 'bg-card text-foreground/80 hover:bg-muted'
      )}
    >
      {children}
    </button>
  );
}

/** Lote C1 (punto 8): los orígenes del filtro, en el orden en que se ofrecen. */
const OPCIONES_DE_ORIGEN: Array<{ valor: OrigenDeLaPropuesta; texto: string }> = [
  { valor: 'ESTRATEGIA', texto: 'Estrategia' },
  { valor: 'RECALCULO', texto: 'Recálculo (sin estrategia)' },
  { valor: 'VUELTA', texto: 'Vuelta de una vencida' },
];

/** Lote B1 (D2): el filtro de Descartadas, en el orden de la ventana. */
const OPCIONES_DE_MOTIVO: Array<{ valor: FiltroDeMotivo; texto: string }> = [
  ...MOTIVOS_DE_DESCARTE.map((m) => ({ valor: m.clave, texto: m.texto })),
  { valor: 'SIN_MOTIVO', texto: 'Sin motivo' },
  { valor: 'SOLA', texto: 'Se descartó sola' },
];

/** Un filtro puesto, con su × para sacarlo (lote B1). */
function ChipDeFiltro({ texto, onSacar }: { texto: string; onSacar: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-0.5 text-xs">
      {texto}
      <button
        type="button"
        aria-label={`Sacar el filtro ${texto}`}
        onClick={onSacar}
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function textoDesenlace(clave: string): string {
  switch (clave) {
    case 'APLICADA':
      return 'Aplicadas';
    case 'DESCARTADA':
      return 'Descartadas';
    case 'PRECIO_CAMBIO':
      return 'El precio ya había cambiado';
    case 'YA_RESUELTA':
      return 'Alguien las resolvió antes';
    case 'NO_EXISTE':
      return 'No se encontraron';
    case 'COSTO_CAMBIO':
      return 'El costo ya había cambiado';
    // Saneamiento del 26-sep (T7): el precio que se vio no era el que se iba a aplicar.
    case 'PROPUESTA_CAMBIO':
      return 'La propuesta cambió mientras la mirabas';
    // El lote nunca manda salida: acá SALIDA_INVALIDA es un precio imposible
    // (cero o negativo, guardado antes del saneamiento), no una clave mala.
    case 'SALIDA_INVALIDA':
      return 'Ese precio no es posible: revisá el margen o descartala';
    // Lote A del 2-oct (A1): no se aplican en lote; cada una tiene su «Aprobar esta».
    case 'DEBAJO_DEL_COSTO':
      return 'Debajo del costo, van una por una';
    case 'VIEJA':
      return 'Calculadas antes de que cambie el mercado, van una por una';
    // S3 (8-oct): usaron el precio de una tienda que ya no está entre las de la empresa.
    case 'TIENDA_QUITADA':
      return 'Usaron una tienda que ya no está entre las tuyas, van una por una';
    default:
      return 'Con error';
  }
}

/** Un vacío que explica por qué está vacío, en vez de una tabla sin filas. */
function Vacio({
  estado,
  conBusqueda,
  conFiltro,
  onQuitarFiltro,
}: {
  estado: ProposalStatus;
  conBusqueda: boolean;
  conFiltro: boolean;
  onQuitarFiltro: () => void;
}) {
  if (conBusqueda) {
    return (
      <EstadoVacio icono={Search} titulo="Ningún producto coincide con la búsqueda">
        Probá con otro nombre o código.
      </EstadoVacio>
    );
  }

  // Con un filtro puesto, los textos de abajo mentirían («no hay nada para
  // decidir», «todas las estrategias avisan antes»): puede haber otras.
  if (conFiltro) {
    return (
      <EstadoVacio
        icono={Inbox}
        titulo="Ninguna con este filtro."
        accion={
          <Button variant="outline" size="sm" onClick={onQuitarFiltro}>
            Quitar el filtro
          </Button>
        }
      />
    );
  }

  const texto: Record<ProposalStatus, { titulo: string; detalle: string }> = {
    PENDING: {
      titulo: 'No hay nada para decidir',
      detalle:
        'Puede ser porque todavía no hay ninguna estrategia, o porque nada cambió desde la última revisión (un precio de la competencia, un costo o el precio de lista). Cuando algo cambie, la propuesta aparece acá sola.',
    },
    APPLIED: {
      titulo: 'Todavía no se aprobó ninguna',
      detalle: 'Las propuestas que apruebes van a quedar registradas acá.',
    },
    DISCARDED: {
      titulo: 'Todavía no se descartó ninguna',
      detalle: 'Las que descartes quedan registradas acá, con quién y cuándo.',
    },
    AUTO_APPLIED: {
      titulo: 'Ninguna se aplicó sola',
      detalle:
        'Todas las estrategias están en modo «avisame antes», así que ningún precio se movió sin aprobación.',
    },
  };

  const t = texto[estado];
  return (
    <EstadoVacio icono={Inbox} titulo={t.titulo}>
      {t.detalle}
    </EstadoVacio>
  );
}

export default ProposalsInboxTab;
